// src/services/usageService.js
// ============================================
// UNIFIED USAGE TRACKING SERVICE
// ============================================
//
// This service is the single source of truth for plan limits, usage
// tracking, and credit deduction across the Outlne platform.
//
// CONCEPTS
// --------
// - Iteration : a single AI generation call (e.g., one inpainting pass).
// - Final Image : a completed, exported design. On average, ~10 iterations
//   are needed to produce one final image. The platform tracks both
//   iterations consumed and final images explicitly saved.
// - Video : a generated motion clip. Tracked as a separate resource.
//
// PLAN CAPS
// ---------
// Each plan defines three hard limits:
//   iterationCap   – total iterations allowed per billing cycle
//   videoCap       – total videos allowed per billing cycle
//   finalImageCap  – derived as floor(iterationCap / ITERATIONS_PER_FINAL_IMAGE)
//
// Additionally, each plan has a genBudget – the dollar balance loaded
// on the user's account to cover generation costs. The genBudget is
// set to the exact cost of a target content bundle (videos + images)
// so that a user creating the intended mix exhausts the balance
// perfectly.
//
// Current monthly plan values:
//   Starter      :  77 iterations /  2 videos / genBudget $5.04
//   Creator      : 342 iterations / 12 videos / genBudget $22.28
//   Creator Plus : 475 iterations / 17 videos / genBudget $30.90
//   Pro          : 765 iterations / 27 videos / genBudget $49.76
//
// Annual plans use the same monthly caps; genBudget is 12× the monthly value.
/*
 * NOTE: The generation budget is a single pool shared between
 * iterations and videos.  A user cannot max out both caps
 * simultaneously.  For example, on Creator ($22.28 budget):
 *   - 342 iterations (34 final images) costs $22.23, leaving ~$0.05
 *   - 12 videos costs $21.48, leaving ~$0.80, enough for ~1 final image
 *
 * The "real world value" shown on the Welcome screen reflects the
 * achievable value range based on the target bundle (videos + images)
 * that the plan is designed to zero-out. Image-only and video-only
 * fallback values are also available.
 *
 * Video value: $75 – $150 per short-form clip (nano‑creator / UGC rates).
 * Image value: $35 per final design.
 */
//
//
// KEY FUNCTIONS
// -------------
// - getUserPlanCaps(userData) : returns the plan caps for a user,
//   resolving monthly vs annual billing.
// - getRemainingCredits(userData) : returns current remaining
//   iterations, videos, and final images, plus plan caps.
// - checkSufficientCredits(userData, requiredCount, type) :
//   verifies whether the user has enough iterations (type='image')
//   or videos (type='video') for an operation.
// - deductCredits / deductImageCredits / deductVideoCredits :
//   atomically increment the appropriate usage counter in Firestore.
// - finalizeImage(userId, count) : bumps the finalImagesGenerated
//   counter, respecting the final image cap.
// - startCreditPolling / verifyCreditsRealtime : for live UI updates.
// - safeGenerate : wraps generation with pre‑check, deduction, and
//   automatic rollback on failure.
//
// - getPlanValueRange(planName) : returns professional value range
//   for the plan's target bundle.
// - getRealWorldValueProduced(userData) : professional value of
//   generated assets (final images + videos).
// - getRemainingRealWorldValue(userData) : target high value minus
//   produced value (responsive deduction).
//
// All usage counters are plain integers. There are no fractional
// iterations, so the remaining count is always exact.
// ============================================



import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// ============================================
// CONSTANTS
// ============================================

/** Average inpainting iterations needed to produce one finalized image */
export const ITERATIONS_PER_FINAL_IMAGE = 10;

const CREDIT_VALUE = 0.01;               // 1 credit = $0.01
export const IMAGE_COST_CREDITS = 6.5;    // per iteration
export const VIDEO_COST_CREDITS = 179;    // per final video

// ============================================
// MARKET REFERENCE VALUES (real‑world replacement)
// ============================================

export const IMAGE_MARKET_RATE = 35;          // $35 per final image
export const VIDEO_MARKET_RATE_LOW = 75;      // $75 per short‑form clip
export const VIDEO_MARKET_RATE_HIGH = 150;    // $150 per short‑form clip

// ============================================
// TARGET BUNDLES (zero‑out mix)
// ============================================

export const TARGET_BUNDLES = {
  starter:     { videos: 1,  images: 5 },
  creator:     { videos: 7,  images: 15 },
  creatorplus: { videos: 10, images: 20 },
  pro:         { videos: 14, images: 38 },
};

// ============================================
// PLAN CAPS & BUDGET
// (iteration caps, video caps, genBudget from economics doc)
// ============================================

/**
 * Create plan definition.
 * @param {string} name
 * @param {number} iterationCap - total iterations allowed per month
 * @param {number} videoCap - total videos allowed per month
 * @param {number} genBudget - dollar balance loaded on account
 * @param {string} description - user‑facing plan description
 */
function buildPlan(name, iterationCap, videoCap, genBudget, description) {
  const finalImageCap = Math.floor(iterationCap / ITERATIONS_PER_FINAL_IMAGE);
  return {
    name,
    iterationCap,
    videoCap,
    finalImageCap,
    genBudget,   // exact dollar amount from economics
    description,
  };
}

export const PLAN_CAPS = {
  starter: buildPlan(
    'Starter',
    77,
    2,
    5.04,
    '3‑day free trial. Complete access to our Interior Design Editor tools.'
  ),
  creator: buildPlan(
    'Creator',
    342,
    12,
    22.28,
    'For renters, homeowners, and designers decorating apartments and personal spaces.'
  ),
  creatorplus: buildPlan(
    'Creator Plus',
    475,
    17,
    30.90,
    'Exclusive gifted‑only plan with expanded capacity for design professionals.'
  ),
  pro: buildPlan(
    'Pro',
    765,
    27,
    49.76,
    'For anyone who wants the ultimate Interior Design tool with pro‑level features.'
  ),
};

// Annual variants – same monthly caps, annual budget = monthly * 12
function annualize(plan) {
  return {
    ...plan,
    genBudget: Math.round(plan.genBudget * 12 * 100) / 100,
  };
}

export const PLAN_CAPS_ANNUAL = {
  creator: annualize(PLAN_CAPS.creator),
  creatorplus: annualize(PLAN_CAPS.creatorplus),
  pro: annualize(PLAN_CAPS.pro),
};

const DEFAULT_CAPS = PLAN_CAPS.starter;

// ============================================
// COST CALCULATIONS (per iteration / video)
// ============================================

export const calculateImageGenerationCost = (options = {}) => {
  const { resolution = 'standard', model = 'standard' } = options;
  let cost = IMAGE_COST_CREDITS;
  if (resolution === 'hd') cost *= 1.5;
  else if (resolution === '4k') cost *= 2.5;
  if (model === 'premium') cost *= 1.5;
  return Math.ceil(cost);
};

export const calculateVideoGenerationCost = (options = {}) => {
  const { duration = 10, resolution = '1080p' } = options;
  const baseCostPerSec = VIDEO_COST_CREDITS / 10; // 17.9 credits/s
  let cost = duration * baseCostPerSec;
  if (resolution === '4k') cost *= 1.8;
  else if (resolution === '1080p') cost *= 1.2;
  return Math.ceil(cost);
};

export const calculateAssetUploadCost = (options = {}) => {
  const { fileSizeMB = 0, assetType = 'image' } = options;
  if (assetType === '3d') return Math.ceil(fileSizeMB / 2) || 2;
  return Math.ceil(fileSizeMB / 5) || 1;
};

// ============================================
// HELPER
// ============================================
const safeParseNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? defaultValue : parsed;
  }
  return defaultValue;
};

// ============================================
// PLAN LIMITS & REMAINING CALCULATIONS
// ============================================

/**
 * Get the plan caps for a user, resolving monthly vs annual.
 * Returns object with { name, iterationCap, videoCap, finalImageCap, genBudget, description }
 */
export const getUserPlanCaps = (userData) => {
  if (!userData) return DEFAULT_CAPS;

  const planName = (
    userData.subscriptionPlan ||
    userData.subscription?.planName ||
    userData.pendingPlan?.name ||
    'starter'
  ).toLowerCase();

  const billingCycle =
    userData.subscription?.billingCycle ||
    userData.pendingPlan?.billingCycle ||
    'monthly';

  // Normalize: remove spaces and convert to lowercase key
  // e.g. "Creator Plus" -> "creatorplus", "pro" -> "pro"
  const normalized = planName.replace(/\s+/g, '').toLowerCase();

  if (billingCycle === 'yearly' || billingCycle === 'annual') {
    return PLAN_CAPS_ANNUAL[normalized] || PLAN_CAPS[normalized] || DEFAULT_CAPS;
  }
  return PLAN_CAPS[normalized] || DEFAULT_CAPS;
};

/**
 * Get remaining counts for the dashboard.
 * @returns {Object} {
 *   iterations: remaining iterations,
 *   videos: remaining final videos,
 *   finalImages: remaining final images (derived from iteration usage),
 *   iterationCap, videoCap, finalImageCap, genBudget
 * }
 */
export const getRemainingCredits = (userData) => {
  if (!userData) {
    return {
      iterations: 0,
      videos: 0,
      finalImages: 0,
      iterationCap: 0,
      videoCap: 0,
      finalImageCap: 0,
      genBudget: 0,
    };
  }

  const caps = getUserPlanCaps(userData);

  // Read usage counters
  const iterationsUsed = safeParseNumber(userData.usage?.iterationsGenerated, 0);
  const finalImagesUsed = safeParseNumber(userData.usage?.finalImagesGenerated, 0);
  const videosUsed = safeParseNumber(userData.usage?.videosGenerated, 0);

  const remainingIterations = Math.max(0, caps.iterationCap - iterationsUsed);
  const remainingVideos = Math.max(0, caps.videoCap - videosUsed);

  // Final images remaining: cap minus used, also cannot exceed what iterations can support.
  const finalCap = caps.finalImageCap;
  const remainingFinalImages = Math.min(
    Math.max(0, finalCap - finalImagesUsed),
    Math.floor(remainingIterations / ITERATIONS_PER_FINAL_IMAGE)
  );

  return {
    iterations: remainingIterations,
    videos: remainingVideos,
    finalImages: remainingFinalImages,
    iterationCap: caps.iterationCap,
    videoCap: caps.videoCap,
    finalImageCap: finalCap,
    genBudget: caps.genBudget,
    // legacy fields for compatibility
    totalImageEquivalents: caps.iterationCap,
    usedImageEquivalents: iterationsUsed,
    imageCap: caps.iterationCap,   // some components might still use this
  };
};

/**
 * Check if user has sufficient resources for an operation.
 * @param {Object} userData
 * @param {number} requiredCount - number of iterations (for image) or videos (for video)
 * @param {string} type - 'image' (means iteration) or 'video'
 * @returns {{ hasCredits: boolean, remaining: number, message: string }}
 */
export const checkSufficientCredits = (userData, requiredCount, type = 'image') => {
  const remaining = getRemainingCredits(userData);
  let has = false;
  let rem = 0;
  let msg = '';

  if (type === 'image') {
    has = remaining.iterations >= requiredCount;
    rem = remaining.iterations;
    msg = has
      ? `You have ${remaining.iterations} iterations remaining.`
      : `You need ${requiredCount} iterations but only ${remaining.iterations} left.`;
  } else if (type === 'video') {
    has = remaining.videos >= requiredCount;
    rem = remaining.videos;
    msg = has
      ? `You have ${remaining.videos} videos remaining.`
      : `You need ${requiredCount} videos but only ${remaining.videos} left.`;
  }

  return { hasCredits: has, remaining: rem, message: msg };
};

// ============================================
// CREDIT DEDUCTION (updates Firestore counters)
// ============================================

/**
 * Deduct usage for a single image iteration or a video.
 * Increments iterationsGenerated (for images) or videosGenerated (for videos).
 * @param {string} userId
 * @param {number} cost - number of iterations/videos to deduct
 * @param {Object} options - { type: 'image'|'video', metadata }
 * @returns {Promise<Object>}
 */
export const deductCredits = async (userId, cost, options = {}) => {
  if (!userId) return { success: false, error: 'No userId' };
  if (cost <= 0) return { success: true, cost: 0, message: 'No deduction needed' };

  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return { success: false, error: 'User not found' };

    const userData = userSnap.data();
    const type = options.type || 'image';

    let fieldToUpdate;
    if (type === 'video') {
      fieldToUpdate = 'usage.videosGenerated';
    } else {
      fieldToUpdate = 'usage.iterationsGenerated';
    }

    const currentValue = safeParseNumber(
      userData?.usage?.[fieldToUpdate.split('.').pop()] ?? 0,
      0
    );
    const newValue = currentValue + cost;

    const updateData = {
      [fieldToUpdate]: newValue,
      'usage.lastUpdatedAt': serverTimestamp(),
    };

    await updateDoc(userRef, updateData);
    console.log(`Deducted ${cost} ${type}(s). New ${fieldToUpdate}: ${newValue}`);
    return { success: true, cost, message: `Deducted ${cost} ${type}(s)` };
  } catch (error) {
    console.error('Error deducting credits:', error);
    return { success: false, error: error.message };
  }
};

export const deductImageCredits = async (userId, cost = 1, metadata = {}) => {
  return deductCredits(userId, cost, { type: 'image', metadata });
};

export const deductVideoCredits = async (userId, cost = 1, metadata = {}) => {
  return deductCredits(userId, cost, { type: 'video', metadata });
};

/**
 * Finalize an image: increment finalImagesGenerated and optionally validate against cap.
 * Should be called when user saves/finalizes an inpainting result.
 */
export const finalizeImage = async (userId, count = 1) => {
  if (!userId || count <= 0) return { success: false, error: 'Invalid request' };
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return { success: false, error: 'User not found' };

    const userData = userSnap.data();
    const caps = getUserPlanCaps(userData);
    const currentFinal = safeParseNumber(userData?.usage?.finalImagesGenerated, 0);
    const newFinal = currentFinal + count;

    if (newFinal > caps.finalImageCap) {
      return { success: false, error: `Cannot finalize: would exceed final image cap (${caps.finalImageCap})` };
    }

    await updateDoc(userRef, {
      'usage.finalImagesGenerated': newFinal,
      'usage.lastUpdatedAt': serverTimestamp(),
    });
    return { success: true, newFinalCount: newFinal };
  } catch (error) {
    console.error('Error finalizing image:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// ROLLBACK & TIMEOUT SUPPORT
// ============================================

const pendingDeductions = new Map();

export const deductCreditsWithTimeout = async (userId, cost, options = {}, timeoutMs = 30000) => {
  if (!userId) return { success: false, error: 'No userId' };
  if (cost <= 0) return { success: true, cost: 0, message: 'No deduction' };

  const deductionId = `${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  try {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Deduction timeout')), timeoutMs)
    );
    const result = await Promise.race([deductCredits(userId, cost, options), timeoutPromise]);

    if (result.success) {
      pendingDeductions.set(deductionId, { userId, cost, options, timestamp: Date.now(), result });
      setTimeout(() => pendingDeductions.delete(deductionId), 300000); // 5 min
      return { ...result, deductionId, rollback: async () => await rollbackDeduction(deductionId) };
    }
    return result;
  } catch (error) {
    return { success: false, error: error.message, timedOut: true };
  }
};

export const rollbackDeduction = async (deductionId) => {
  const pending = pendingDeductions.get(deductionId);
  if (!pending) return { success: false, error: 'No pending deduction or already rolled back' };
  if (Date.now() - pending.timestamp > 300000) {
    pendingDeductions.delete(deductionId);
    return { success: false, error: 'Rollback window expired' };
  }

  try {
    const userRef = doc(db, 'users', pending.userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return { success: false, error: 'User not found' };

    const userData = userSnap.data();
    const field = pending.options.type === 'video' ? 'videosGenerated' : 'iterationsGenerated';
    const current = safeParseNumber(userData?.usage?.[field], 0);
    const newValue = Math.max(0, current - pending.cost);

    await updateDoc(userRef, {
      [`usage.${field}`]: newValue,
      'usage.lastUpdatedAt': serverTimestamp(),
    });

    pendingDeductions.delete(deductionId);
    return { success: true, cost: pending.cost, message: 'Credits restored' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ============================================
// POLLING & VERIFICATION
// ============================================

export const startCreditPolling = (userId, onUpdate, intervalMs = 5000) => {
  if (!userId) return () => {};

  let intervalId = null;
  let lastKnown = null;

  const poll = async () => {
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      if (userSnap.exists()) {
        const data = userSnap.data();
        const iterations = safeParseNumber(data.usage?.iterationsGenerated, 0);
        const videos = safeParseNumber(data.usage?.videosGenerated, 0);
        const finals = safeParseNumber(data.usage?.finalImagesGenerated, 0);
        if (lastKnown !== null &&
            (iterations !== lastKnown.iterations ||
             videos !== lastKnown.videos ||
             finals !== lastKnown.finals)) {
          onUpdate(getRemainingCredits(data));
        }
        lastKnown = { iterations, videos, finals };
      }
    } catch (err) {
      console.warn('Polling error:', err);
    }
  };

  intervalId = setInterval(poll, intervalMs);
  return () => clearInterval(intervalId);
};

export const verifyCreditsRealtime = async (userId, requiredCount, type = 'image') => {
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) return { hasCredits: false, remaining: 0, message: 'User not found' };
    return checkSufficientCredits(userSnap.data(), requiredCount, type);
  } catch (error) {
    return { hasCredits: false, remaining: 0, message: error.message };
  }
};

// ============================================
// SAFE GENERATION WRAPPER
// ============================================

export const safeGenerate = async (userId, cost, generationFn, options = {}) => {
  const { onStart, onProgress, onComplete, onError, type = 'image' } = options;

  const verification = await verifyCreditsRealtime(userId, cost, type);
  if (!verification.hasCredits) {
    if (onError) onError(verification.message);
    return { success: false, error: verification.message };
  }

  const deduction = await deductCreditsWithTimeout(userId, cost, { generationType: type });
  if (!deduction.success) {
    if (onError) onError(deduction.error);
    return { success: false, error: deduction.error };
  }

  if (onStart) onStart();

  try {
    const result = await generationFn(onProgress);
    if (onComplete) onComplete(result);
    return { success: true, result, deductionId: deduction.deductionId };
  } catch (error) {
    console.error('Generation failed, rolling back:', error);
    const rollback = await rollbackDeduction(deduction.deductionId);
    if (onError) {
      onError(`Generation failed: ${error.message}. ${rollback.success ? 'Credits restored.' : 'Manual review needed.'}`);
    }
    return { success: false, error: error.message, rolledBack: rollback.success };
  }
};

// ============================================
// REAL‑WORLD VALUE COUNTER (professional output)
// ============================================

/**
 * Get the professional market value range for a plan's target bundle.
 * @param {string} planName - 'starter', 'creator', 'creatorplus', or 'pro'
 * @returns {{ low: number, high: number }}
 */
export const getPlanValueRange = (planName) => {
  // Normalize to match bundle keys (removes spaces, lowercase)
  const key = (planName || '').replace(/\s+/g, '').toLowerCase();
  const bundle = TARGET_BUNDLES[key] || TARGET_BUNDLES.starter;
  const low = bundle.videos * VIDEO_MARKET_RATE_LOW + bundle.images * IMAGE_MARKET_RATE;
  const high = bundle.videos * VIDEO_MARKET_RATE_HIGH + bundle.images * IMAGE_MARKET_RATE;
  return { low, high };
};

/**
 * Professional value of assets the user has actually generated.
 * @param {Object} userData - Firestore user document
 * @returns {number} total value in dollars (final images × $35 + videos × $150)
 */
export const getRealWorldValueProduced = (userData) => {
  if (!userData) return 0;
  const finalImages = safeParseNumber(userData.usage?.finalImagesGenerated, 0);
  const videos = safeParseNumber(userData.usage?.videosGenerated, 0);
  return finalImages * IMAGE_MARKET_RATE + videos * VIDEO_MARKET_RATE_HIGH;
};

/**
 * Remaining real‑world value: the target bundle’s high value minus
 * what has already been produced. This number decreases as the user
 * creates content, providing a responsive “wallet” of creative worth.
 * @param {Object} userData
 * @returns {number} remaining value (minimum 0)
 */
export const getRemainingRealWorldValue = (userData) => {
  if (!userData) return 0;
  const planName = (
    userData.subscriptionPlan ||
    userData.subscription?.planName ||
    userData.pendingPlan?.name ||
    'starter'
  ).toLowerCase();

  // Normalize again for consistency
  const key = planName.replace(/\s+/g, '').toLowerCase();
  const { high } = getPlanValueRange(key);
  const produced = getRealWorldValueProduced(userData);
  return Math.max(0, high - produced);
};

// ============================================
// UTILS
// ============================================

export const fetchUserUsage = async (userId) => {
  if (!userId) return null;
  try {
    const snap = await getDoc(doc(db, 'users', userId));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error('fetchUserUsage error:', error);
    return null;
  }
};

export const refreshRemainingCounts = (userData) => {
  return getRemainingCredits(userData);
};

export const getPlanLimitsByName = (planName) => {
  if (!planName) return DEFAULT_CAPS;
  const normalized = planName.toLowerCase() === 'pro' ? 'pro' : planName.toLowerCase();
  return PLAN_CAPS[normalized] || DEFAULT_CAPS;
};

export const getUsagePercentages = (userData) => {
  const caps = getUserPlanCaps(userData);
  const usedIter = safeParseNumber(userData?.usage?.iterationsGenerated, 0);
  const usedVid = safeParseNumber(userData?.usage?.videosGenerated, 0);

  return {
    iterations: Math.min(100, (usedIter / caps.iterationCap) * 100),
    videos: Math.min(100, (usedVid / caps.videoCap) * 100),
  };
};

// ============================================
// EXPORT DEFAULT (for existing imports)
// ============================================
export default {
  PLAN_CAPS,
  IMAGE_COST_CREDITS,
  VIDEO_COST_CREDITS,
  ITERATIONS_PER_FINAL_IMAGE,
  calculateImageGenerationCost,
  calculateVideoGenerationCost,
  calculateAssetUploadCost,
  getUserPlanCaps,
  getRemainingCredits,
  checkSufficientCredits,
  deductCredits,
  deductImageCredits,
  deductVideoCredits,
  finalizeImage,
  deductCreditsWithTimeout,
  rollbackDeduction,
  startCreditPolling,
  verifyCreditsRealtime,
  safeGenerate,
  fetchUserUsage,
  refreshRemainingCounts,
  getPlanLimitsByName,
  getUsagePercentages,
  // new value counter exports
  getPlanValueRange,
  getRealWorldValueProduced,
  getRemainingRealWorldValue,
  TARGET_BUNDLES,
  IMAGE_MARKET_RATE,
  VIDEO_MARKET_RATE_LOW,
  VIDEO_MARKET_RATE_HIGH,
};